import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  computed,
  inject,
  signal
} from '@angular/core';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { ChartModule } from 'primeng/chart';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import {
  ForecastCashFlowService,
  getForecastRange,
  type ForecastHorizon,
  type ForecastPeriod,
  type ForecastCashFlowData
} from '../../services/forecast-cash-flow.service';

@Component({
  selector: 'app-forecast-cash-flow',
  templateUrl: './forecast-cash-flow.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    CurrencyPipe,
    ChartModule,
    TableModule,
    TooltipModule
  ],
  styles: [
    `
      :host ::ng-deep .p-chart {
        display: block;
        width: 100%;
        height: 100%;
      }
      :host ::ng-deep .p-chart canvas {
        max-width: 100% !important;
        max-height: 100% !important;
      }
      /* Tooltip de ayuda (se renderiza fuera del host) */
      ::ng-deep .forecast-help-tooltip.p-tooltip,
      ::ng-deep .p-tooltip.forecast-help-tooltip {
        max-width: 22rem;
      }
      ::ng-deep .forecast-help-tooltip .p-tooltip-text,
      ::ng-deep .p-tooltip.forecast-help-tooltip .p-tooltip-text {
        white-space: pre-line;
        line-height: 1.45;
        font-size: 0.75rem;
        text-align: left;
      }
    `
  ]
})
export class ForecastCashFlowComponent implements OnInit {
  private forecastService = inject(ForecastCashFlowService);

  /** Textos de ayuda por panel (tooltip al hover). */
  readonly help = {
    collected:
      '¿Cuánto ya cobraste en este periodo?\n\n' +
      'Suma los pagos que ya se registraron como cobrados dentro del rango de fechas seleccionado.\n\n' +
      'Sirve para comparar lo que ya entró vs lo que aún esperas cobrar.',

    projection:
      '¿Cuánto dinero esperas recibir en total?\n\n' +
      'Es la suma de todo lo que debería cobrarse en el periodo:\n' +
      '• Cargos pendientes de pago\n' +
      '• Próximas renovaciones de suscripciones\n\n' +
      'Debajo verás el “esperado”: el mismo total, pero ajustado si hay riesgo de que no se cobre (confianza alta/media/baja).',

    mrr:
      '¿Cuánto ingresas al mes de forma recurrente?\n\n' +
      'Toma tus suscripciones activas (y en prueba) y las convierte a un monto mensual equivalente.\n\n' +
      'Ejemplo: una suscripción anual de $12,000 cuenta como $1,000 de MRR.\n\n' +
      'No es lo que cobrarás este mes; es el ritmo mensual de tu negocio recurrente.',

    charges:
      '¿Cuánto ya está agendado para cobrar?\n\n' +
      'Son cobros que ya existen en el sistema (parcialidades, cargos pendientes, etc.) con fecha dentro del periodo.\n\n' +
      'Debajo verás cuánto viene de renovaciones de suscripciones que todavía no se han generado como cargos, pero se proyectan.',

    risk:
      '¿Qué parte del forecast puede no cobrarse?\n\n' +
      'Número grande: montos con mayor riesgo (vencidos, fallidos o suscripciones atrasadas).\n\n' +
      'Debajo: dinero que ya debió cobrarse antes de este periodo y sigue pendiente.\n\n' +
      'Es tu alerta de cobranza.',

    cashflow:
      '¿En qué meses entra y se proyecta el dinero?\n\n' +
      'Cada barra apila:\n' +
      '• Cobrado (ya entró)\n' +
      '• Cargos aún pendientes agendados\n' +
      '• Renovaciones de suscripciones\n' +
      'Línea naranja: lo esperado de la proyección (ajustado por riesgo).\n\n' +
      'Te ayuda a comparar lo que ya entró vs lo que falta.',

    composition:
      '¿Cómo se reparte el dinero del periodo?\n\n' +
      '• Cobrado: ya entró\n' +
      '• Cargos: pendientes calendarizados\n' +
      '• Suscripciones: renovaciones proyectadas\n\n' +
      'Misma lógica que la barra apilada del flujo de caja.',

    method:
      '¿Cómo se va a cobrar?\n\n' +
      'Separa el forecast por forma de cobro: liga de pago, transferencia, manual o cargo automático a tarjeta.\n\n' +
      'Útil para planear la operación del equipo de cobranza.',

    monthly:
      'El mismo flujo de caja, pero en números.\n\n' +
      'Por cada mes ves:\n' +
      '• Cobrado (ya entró)\n' +
      '• Cargos pendientes\n' +
      '• Suscripciones proyectadas\n' +
      '• Total proyectado y esperado\n\n' +
      'Ideal si quieres revisar o compartir cifras exactas.',

    summary:
      'Una lectura rápida del forecast en texto.\n\n' +
      'Resume proyección, composición, meses más fuertes, métodos de cobro y riesgo,\n' +
      'para entender el panorama sin revisar cada gráfica.'
  } as const;

  /** Glosario del pie de página. */
  readonly glossary: { term: string; definition: string }[] = [
    {
      term: 'Periodo',
      definition:
        'Rango de fechas del forecast (mes en curso, 3/6/12 meses o personalizado). Empieza el día 1 del mes actual salvo que elijas fechas custom.'
    },
    {
      term: 'Proyección bruta',
      definition:
        'Suma de todo lo que debería cobrarse en el periodo: cargos pendientes calendarizados más renovaciones proyectadas de suscripciones.'
    },
    {
      term: 'Esperado',
      definition:
        'Misma proyección, pero ponderada por riesgo de cobro (confianza alta/media/baja). Suele ser menor o igual a la proyección bruta.'
    },
    {
      term: 'MRR',
      definition:
        'Monthly Recurring Revenue: ingreso recurrente convertido a equivalente mensual (p. ej. una anualidad de $12,000 = $1,000 de MRR). No es lo que cobrarás este mes concreto.'
    },
    {
      term: 'Cargos calendarizados',
      definition:
        'Cobros que ya existen en el sistema (parcialidades, cargos pendientes, etc.) con fecha de vencimiento dentro del periodo.'
    },
    {
      term: 'Renovaciones / suscripciones proyectadas',
      definition:
        'Ingresos futuros estimados a partir de la siguiente fecha de cobro y la frecuencia de suscripciones activas, en prueba o atrasadas.'
    },
    {
      term: 'Cobrado en el periodo',
      definition:
        'Pagos ya registrados como cobrados cuya fecha de pago cae dentro del rango seleccionado. Sirve para comparar lo que ya entró vs lo proyectado.'
    },
    {
      term: 'En riesgo',
      definition:
        'Parte de la proyección con menor confianza de cobro (cargos vencidos/fallidos o suscripciones atrasadas).'
    },
    {
      term: 'Vencido fuera del periodo',
      definition:
        'Dinero que debió cobrarse antes del inicio del periodo y sigue pendiente. No entra en la proyección del periodo, pero es alerta de cobranza.'
    },
    {
      term: 'Método de cobro',
      definition:
        'Cómo se espera cobrar: liga de pago, transferencia, manual o cargo recurrente a tarjeta.'
    }
  ];

  data = signal<ForecastCashFlowData | null>(null);
  period = signal<ForecastPeriod>({ horizon: '6m' });
  customDateFrom = signal('');
  customDateTo = signal('');
  glossaryOpen = signal(false);

  toggleGlossary(): void {
    this.glossaryOpen.update((v) => !v);
  }

  readonly horizonOptions: { value: ForecastHorizon; label: string }[] = [
    { value: 'month', label: 'Mes en curso' },
    { value: '3m', label: '3 meses' },
    { value: '6m', label: '6 meses' },
    { value: '12m', label: '12 meses' },
    { value: 'custom', label: 'Personalizado' }
  ];

  readonly periodLabel = computed(() => {
    const { from, to } = getForecastRange(this.period());
    const fmt = (d: Date) =>
      d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' });
    return `${fmt(from)} – ${fmt(to)}`;
  });



  /** Resumen narrativo del forecast (reemplaza el panel “Por frecuencia”). */
  readonly narrativeSummary = computed(() => {
    const d = this.data();
    if (!d) {
      return ['Cargando proyección…'];
    }
    if (d.error && !(d.kpis?.totalProjected || d.months?.length)) {
      return ['No se pudo armar el resumen porque falló la carga del forecast.'];
    }

    const k = d.kpis;
    const months = d.months ?? [];
    const methods = d.byCollectionMethod ?? [];
    const freqs = d.byFrequency ?? [];
    const paragraphs: string[] = [];

    const total = k.totalProjected ?? 0;
    const expected = k.totalExpected ?? 0;
    const gap = Math.max(0, total - expected);
    const gapPct = total > 0 ? Math.round((gap / total) * 100) : 0;

    paragraphs.push(
      `En el periodo ${this.periodLabel()} se proyectan ${this.formatMxn(total)} ` +
        `en ${k.eventCount ?? 0} cobros futuros. Tras ponderar por riesgo de cobro, ` +
        `el esperado queda en ${this.formatMxn(expected)}` +
        (gap > 0 ? ` (≈${gapPct}% menos que la proyección bruta).` : '.')
    );

    const charges = k.chargesProjected ?? 0;
    const subs = k.subscriptionsProjected ?? 0;
    if (total > 0) {
      const chargePct = Math.round((charges / total) * 100);
      const subPct = 100 - chargePct;
      paragraphs.push(
        `La composición es ${chargePct}% cargos ya calendarizados (${this.formatMxn(charges)}) ` +
          `y ${subPct}% renovaciones de suscripciones (${this.formatMxn(subs)}). ` +
          `El MRR equivalente es ${this.formatMxn(k.mrr ?? 0)} ` +
          `con ${k.activeSubscriptions ?? 0} suscripciones activas o en prueba.`
      );
    } else {
      paragraphs.push(
        'No hay ingresos proyectados en este periodo. Revisa si tienes cargos pendientes o suscripciones con próxima fecha de cobro.'
      );
    }

    const withMoney = months.filter((m) => (m.totalAmount ?? 0) > 0);
    if (withMoney.length > 0) {
      const strongest = withMoney.reduce((a, b) => (a.totalAmount >= b.totalAmount ? a : b));
      const weakest = withMoney.reduce((a, b) => (a.totalAmount <= b.totalAmount ? a : b));
      if (strongest.month === weakest.month) {
        paragraphs.push(
          `El flujo se concentra en ${strongest.label} con ${this.formatMxn(strongest.totalAmount)} proyectados.`
        );
      } else {
        paragraphs.push(
          `El mes más fuerte es ${strongest.label} (${this.formatMxn(strongest.totalAmount)}) ` +
            `y el más flojo con actividad es ${weakest.label} (${this.formatMxn(weakest.totalAmount)}).`
        );
      }
    }

    if (methods.length > 0 && total > 0) {
      const top = [...methods].sort((a, b) => b.amount - a.amount)[0];
      const topPct = Math.round((top.amount / total) * 100);
      const others = methods
        .filter((m) => m.method !== top.method)
        .slice(0, 2)
        .map((m) => `${this.methodLabel(m.method)} (${this.formatMxn(m.amount)})`);
      paragraphs.push(
        `Por método de cobro destaca ${this.methodLabel(top.method)} ` +
          `con ${this.formatMxn(top.amount)} (${topPct}% del total)` +
          (others.length ? `; también figuran ${others.join(' y ')}.` : '.')
      );
    }

    if (freqs.length > 0) {
      const topF = [...freqs].sort((a, b) => b.amount - a.amount)[0];
      const freqBits = freqs
        .slice(0, 3)
        .map((f) => `${this.frequencyLabel(f.frequency).toLowerCase()} (${f.count})`);
      paragraphs.push(
        `Las renovaciones se concentran en ciclos ${this.frequencyLabel(topF.frequency).toLowerCase()} ` +
          `(${this.formatMxn(topF.amount)}). Ritmos presentes: ${freqBits.join(', ')}.`
      );
    }

    const collected = k.collectedInHorizon ?? 0;
    const atRisk = k.atRiskAmount ?? 0;
    const overdue = k.overdueAmount ?? 0;
    paragraphs.push(
      `Ya cobrado en el periodo: ${this.formatMxn(collected)}. ` +
        `En riesgo dentro de la proyección: ${this.formatMxn(atRisk)}. ` +
        `Vencido fuera del periodo (sigue pendiente): ${this.formatMxn(overdue)}.`
    );

    return paragraphs;
  });

  readonly cashflowChart = computed(() => {
    const months = this.data()?.months ?? [];
    return {
      labels: months.map((m) => m.label),
      datasets: [
        {
          type: 'bar' as const,
          label: 'Cobrado',
          backgroundColor: 'rgba(15, 118, 110, 0.9)',
          data: months.map((m) => m.collectedAmount),
          borderRadius: 4,
          stack: 'cash',
          order: 2
        },
        {
          type: 'bar' as const,
          label: 'Cargos calendarizados',
          backgroundColor: 'rgba(16, 185, 129, 0.85)',
          data: months.map((m) => m.chargesAmount),
          borderRadius: 4,
          stack: 'cash',
          order: 2
        },
        {
          type: 'bar' as const,
          label: 'Renovaciones (proyección)',
          backgroundColor: 'rgba(14, 165, 233, 0.85)',
          data: months.map((m) => m.subscriptionsAmount),
          borderRadius: 4,
          stack: 'cash',
          order: 2
        },
        {
          type: 'line' as const,
          label: 'Esperado (ponderado)',
          borderColor: 'rgba(245, 158, 11, 0.95)',
          backgroundColor: 'rgba(245, 158, 11, 0.15)',
          borderWidth: 2.5,
          tension: 0.35,
          pointRadius: 3.5,
          pointHoverRadius: 5,
          fill: false,
          order: 1,
          data: months.map((m) => m.expectedAmount)
        }
      ]
    };
  });

  readonly cashflowOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index' as const, intersect: false },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: { boxWidth: 12, font: { size: 11 } }
      },
      tooltip: {
        callbacks: {
          label: (ctx: { dataset: { label?: string }; parsed: { y: number | null } }) => {
            const v = ctx.parsed.y ?? 0;
            return `${ctx.dataset.label}: ${this.formatMxn(v)}`;
          }
        }
      }
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        ticks: { color: '#64748b', font: { size: 11 } }
      },
      y: {
        stacked: true,
        beginAtZero: true,
        grid: { color: 'rgba(15, 23, 42, 0.06)' },
        ticks: {
          color: '#64748b',
          font: { size: 11 },
          callback: (value: string | number) => this.formatMxn(Number(value))
        }
      }
    }
  };

  readonly mixChart = computed(() => {
    const k = this.data()?.kpis;
    const collected = k?.collectedInHorizon ?? 0;
    const charges = k?.chargesProjected ?? 0;
    const subs = k?.subscriptionsProjected ?? 0;
    return {
      labels: ['Cobrado', 'Cargos', 'Suscripciones'],
      datasets: [
        {
          data: [collected, charges, subs],
          backgroundColor: [
            'rgba(15, 118, 110, 0.9)',
            'rgba(16, 185, 129, 0.9)',
            'rgba(14, 165, 233, 0.9)'
          ],
          borderWidth: 0,
          hoverOffset: 2
        }
      ]
    };
  });

  private readonly doughnutColors = [
    'rgba(14, 165, 233, 0.9)',
    'rgba(16, 185, 129, 0.9)',
    'rgba(245, 158, 11, 0.9)',
    'rgba(99, 102, 241, 0.9)',
    'rgba(244, 63, 94, 0.85)'
  ];

  readonly mixLegend = computed(() => {
    const k = this.data()?.kpis;
    return [
      { label: 'Cobrado', amount: k?.collectedInHorizon ?? 0, color: 'rgba(15, 118, 110, 0.9)' },
      { label: 'Cargos', amount: k?.chargesProjected ?? 0, color: 'rgba(16, 185, 129, 0.9)' },
      { label: 'Suscripciones', amount: k?.subscriptionsProjected ?? 0, color: 'rgba(14, 165, 233, 0.9)' }
    ];
  });

  readonly methodLegend = computed(() => {
    const rows = this.data()?.byCollectionMethod ?? [];
    return rows.map((r, i) => ({
      label: this.methodLabel(r.method),
      amount: r.amount,
      color: this.doughnutColors[i % this.doughnutColors.length]
    }));
  });

  readonly doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '62%',
    layout: { padding: 0 },
    plugins: {
      legend: { display: false }
    }
  };

  readonly methodChart = computed(() => {
    const rows = this.data()?.byCollectionMethod ?? [];
    return {
      labels: rows.map((r) => this.methodLabel(r.method)),
      datasets: [
        {
          data: rows.map((r) => r.amount),
          backgroundColor: this.doughnutColors,
          borderWidth: 0,
          hoverOffset: 2
        }
      ]
    };
  });

  ngOnInit(): void {
    this.load();
  }

  setHorizon(horizon: ForecastHorizon): void {
    if (horizon === 'custom') {
      const now = new Date();
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, '0');
      this.customDateFrom.set(`${y}-${m}-${String(now.getDate()).padStart(2, '0')}`);
      const end = new Date(y, now.getMonth() + 6, 0);
      this.customDateTo.set(
        `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`
      );
      this.period.set({ horizon: 'custom' });
      return;
    }
    this.period.set({ horizon });
    this.load();
  }

  applyCustomPeriod(): void {
    const from = this.customDateFrom();
    const to = this.customDateTo();
    if (!from || !to) return;
    this.period.set({ horizon: 'custom', dateFrom: from, dateTo: to });
    this.load();
  }

  load(): void {
    this.data.update((d) =>
      d
        ? { ...d, loading: true, error: undefined }
        : {
            from: '',
            to: '',
            kpis: {
              totalProjected: 0,
              totalExpected: 0,
              chargesProjected: 0,
              subscriptionsProjected: 0,
              mrr: 0,
              atRiskAmount: 0,
              overdueAmount: 0,
              collectedInHorizon: 0,
              activeSubscriptions: 0,
              eventCount: 0
            },
            months: [],
            byFrequency: [],
            byCollectionMethod: [],
            loading: true
          }
    );
    this.forecastService.loadForecast(this.period()).subscribe((d) => this.data.set(d));
  }

  formatMxn(amount: number): string {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  }

  methodLabel(method: string): string {
    const map: Record<string, string> = {
      LINK: 'Liga de pago',
      TRANSFER: 'Transferencia',
      MANUAL: 'Manual',
      RECURRING_CHARGE: 'Cargo recurrente',
      '—': 'Sin método'
    };
    return map[method] || method;
  }

  frequencyLabel(freq: string | undefined): string {
    if (!freq) return '—';
    const map: Record<string, string> = {
      DAILY: 'Diaria',
      WEEKLY: 'Semanal',
      BIWEEKLY: 'Quincenal',
      MONTHLY: 'Mensual',
      QUARTERLY: 'Trimestral',
      SEMIANNUALLY: 'Semestral',
      ANNUALLY: 'Anual',
      ONCE: 'Única'
    };
    return map[freq] || freq;
  }
}
